import { Form, Input, message, Tooltip } from "antd";
import TextArea from "antd/lib/input/TextArea";
import FeatureSpecSectionDropdown from "components/feature-spec-section-dropdown";
import LBSSelectField from "components/lbs-select-field/lbs-select-field";
import RedirectIcon from "components/svg-icons/redirect-icon";
import { ProjectPermissionEnum } from "constants/index";
import {
  isPermissionNotGrantted,
  ProjectContext
} from "context/ProjectProvider";
import { TFetureDetailUpdateAction } from "entity-app/models";
import { useLocationBreakdownStructure } from "hooks/location-breakdown-structure";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { updatePropertyAtPath } from "utils/utils";

export default function MaterialGeneralInfoCard(props: {
  featureInstanceData: any;
  actions: TFetureDetailUpdateAction;
  disabled: boolean;
  materialHeaderMap: any;
  projectId: string;
}) {
  const {
    featureInstanceData,
    actions,
    disabled,
    materialHeaderMap,
    projectId
  } = props;
  const [form] = Form.useForm();
  const [selectedLBSIds, setSelectedLBSIds] = useState<any>([]);
  const { gqlClientForProject, tokenContents, isFeatureFlagEnabled } =
    useContext(ProjectContext);
  const isLBSFeatureEnabled = isFeatureFlagEnabled(
    "LOCATION_BREAKDOWN_STRUCTURE"
  );
  const [localFeatureInstanceData, setLocalFeatureInstanceData] =
    useState<any>();

  useEffect(() => {
    if (!featureInstanceData) return;
    setLocalFeatureInstanceData(structuredClone(featureInstanceData));
  }, [featureInstanceData]);

  const initialLBSIds = useMemo(() => {
    if (
      isLBSFeatureEnabled &&
      featureInstanceData?.feature_locations?.length > 0
    ) {
      const tmpSelectedLBSIds: any = [];
      featureInstanceData?.feature_locations.forEach((item: any) => {
        tmpSelectedLBSIds.push(item.location.id);
      });
      return tmpSelectedLBSIds;
    }
    return [];
  }, [isLBSFeatureEnabled, featureInstanceData]);

  useEffect(() => {
    if (
      isLBSFeatureEnabled &&
      featureInstanceData?.feature_locations?.length > 0
    ) {
      setSelectedLBSIds(initialLBSIds);
    }
  }, [
    initialLBSIds,
    isLBSFeatureEnabled,
    featureInstanceData?.feature_locations,
    featureInstanceData?.feature_locations?.length
  ]);

  const hasPermissionToSetLBSForMaterial = !isPermissionNotGrantted(
    ProjectPermissionEnum.CanSetLBSForMaterial,
    tokenContents?.role!
  );

  const { LBSOptionsMaster, LBSDataInTreeFormat, setLBSForMaterial } =
    useLocationBreakdownStructure(
      gqlClientForProject,
      projectId,
      hasPermissionToSetLBSForMaterial
    );

  const handleLBSFieldChange = useCallback(
    async (ids): Promise<void> => {
      // compare selectedLBSIds with initialLBSIds, if there are no changes, do not update
      if (!ids.length && !initialLBSIds.length) return;
      if (
        ids.length === initialLBSIds.length &&
        ids.every((id: any) => initialLBSIds.includes(id))
      ) {
        return;
      }

      const updateResponse = await setLBSForMaterial(
        [featureInstanceData?.id],
        ids
      );

      if (updateResponse?.success) {
        message.success("Location updated successfully");
      }
    },
    [initialLBSIds, setLBSForMaterial, featureInstanceData?.id]
  );

  const checkValuesUpdated = (key: string, updatedValue: string) => {
    return (featureInstanceData[key] ?? "") !== updatedValue;
  };

  const featureMaterial = useMemo(() => {
    if (!localFeatureInstanceData?.feature_materials?.length) return null;
    return localFeatureInstanceData?.feature_materials[0];
  }, [localFeatureInstanceData?.feature_materials]);

  useEffect(() => {
    if (!featureMaterial) return;
    form.setFieldsValue({
      purchase_order: featureMaterial?.purchase_order,
      tag_name: featureMaterial?.tag_name,
      description: featureMaterial?.description
    });
  }, [featureMaterial, form]);

  return (
    <div
      className="grow h-[396px] overflow-auto"
      data-testid="material-general-info-root"
    >
      <Form
        form={form}
        layout="vertical"
        data-testid="material-general-info-form"
      >
        <Form.Item
          label={materialHeaderMap?.spec_section_no}
          data-testid="material-general-info-spec-section-item"
        >
          <FeatureSpecSectionDropdown
            mode="single"
            disabled={disabled}
            selectedIds={localFeatureInstanceData?.feature_instance_spec_sections.map(
              (specSection: any) => {
                return specSection?.spec_section?.id;
              }
            )}
            onBlur={(ids) => {
              const specSection = localFeatureInstanceData
                ?.feature_instance_spec_sections?.length
                ? localFeatureInstanceData?.feature_instance_spec_sections[0]
                    ?.spec_section
                : {};

              if (ids.includes(specSection?.id)) return;
              actions.updateFeatureInstanceSpecSections({
                ids,
                bidscope: false
              });

              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_instance_spec_sections",
                  [
                    {
                      bid_scope: false,
                      spec_section: {
                        ...specSection,
                        id: ids[0]
                      }
                    }
                  ]
                )
              );
            }}
            data-testid="material-general-info-spec-section-dropdown"
          />
        </Form.Item>
        <Form.Item
          label={materialHeaderMap?.purchase_order}
          name="purchase_order"
          data-testid="material-general-info-purchase-order-item"
        >
          <Input
            placeholder="Enter Purchase Order"
            value={featureMaterial?.purchase_order}
            onBlur={async (event: any) => {
              if (
                (featureMaterial?.purchase_order ?? "") ===
                event?.target?.value?.trim()
              )
                return;
              actions.updateMaterialFields({
                purchase_order: event.target.value?.trim()
              });

              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_materials",
                  [
                    {
                      ...featureMaterial,
                      purchase_order: event.target.value
                    }
                  ]
                )
              );
            }}
            disabled={disabled}
            data-testid="material-general-info-purchase-order-input"
          />
        </Form.Item>
        <Form.Item
          label={materialHeaderMap?.tag_name}
          name="tag_name"
          data-testid="material-general-info-tag-name-item"
        >
          <Input
            value={localFeatureInstanceData?.tag_name}
            placeholder="Enter Material Tag"
            data-testid="material-general-info-tag-name-input"
            onBlur={async (event: any) => {
              if (
                (featureMaterial?.tag_name ?? "") ===
                event?.target?.value?.trim()
              )
                return;
              actions.updateMaterialFields({
                tag_name: event.target.value?.trim()
              });
              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "feature_materials",
                  [
                    {
                      ...featureMaterial,
                      tag_name: event.target.value
                    }
                  ]
                )
              );
            }}
            disabled={disabled}
          />
        </Form.Item>
        <Form.Item
          label={materialHeaderMap?.description}
          data-testid="material-general-info-description-item"
        >
          <TextArea
            disabled={disabled}
            value={localFeatureInstanceData?.description}
            rows={5}
            placeholder="Enter material description"
            data-testid="material-general-info-description-textarea"
            onChange={(event: any) => {
              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "description",
                  event.target.value
                )
              );
            }}
            onBlur={async (event: any) => {
              if (
                !checkValuesUpdated("description", event?.target?.value?.trim())
              )
                return;
              actions.updateFeatureInstanceFields({
                description: event.target.value?.trim()
              });
              setLocalFeatureInstanceData(
                updatePropertyAtPath(
                  localFeatureInstanceData,
                  "description",
                  event.target.value
                )
              );
            }}
          />
        </Form.Item>
        {isLBSFeatureEnabled && (
          <Form.Item
            label={
              <div className="flex justify-between items-center w-full pt-[5px]">
                <span>{materialHeaderMap?.locations}</span>
                {hasPermissionToSetLBSForMaterial && (
                  <Tooltip title="Open Manage Locations">
                    <Link
                      className="flex linking-container--nav__redirectlinkIconLocation w-[80px] text-xs"
                      to={`/project/${projectId}/settings/locations`}
                      target="_blank"
                      data-testid="material-general-info-manage-locations-link"
                    >
                      <div className="font-normal text-black">Manage</div>
                      <div className="ml-1 pt-[2px]">
                        <RedirectIcon size={14} />
                      </div>
                    </Link>
                  </Tooltip>
                )}
              </div>
            }
            data-testid="material-general-info-locations-item"
          >
            <LBSSelectField
              LBSOptionsMaster={LBSOptionsMaster}
              LBSDataInTreeFormat={LBSDataInTreeFormat}
              onChange={(selectedIds: string[]) => {
                setSelectedLBSIds(selectedIds);
              }}
              onBlur={() => {
                handleLBSFieldChange(selectedLBSIds);
              }}
              onClear={() => {
                setSelectedLBSIds([]);
                handleLBSFieldChange([]);
              }}
              disabled={!hasPermissionToSetLBSForMaterial}
              initialValues={selectedLBSIds}
              selectedLBSOptions={featureInstanceData?.feature_locations}
              projectId={projectId}
              data-testid="material-general-info-lbs-select"
            />
          </Form.Item>
        )}
      </Form>
    </div>
  );
}
